import { usePharmacistConfirmDispenseModal } from '@/context/orders/PharmacistOrdersModals';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@montugroup/design-system';
import { Box, FormControlLabel, Stack, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';

export type FormInputs = {
  stockOnHand: boolean;
  RTPMReviewed: boolean;
  dispensedTokenAndPrintedPrescription: boolean;
};

const FORM_DEFAULT_VALUES: FormInputs = {
  stockOnHand: false,
  RTPMReviewed: false,
  dispensedTokenAndPrintedPrescription: false,
};

export function ConfirmDispenseModal() {
  const theme = useTheme();
  const { confirmDispenseModal, confirmDispense, hideConfirmDispenseModal, isSubmitting } =
    usePharmacistConfirmDispenseModal();
  const { register, reset, handleSubmit, formState } = useForm<FormInputs, unknown, NonNullable<FormInputs>>({
    defaultValues: FORM_DEFAULT_VALUES,
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const handleDialogClose = () => {
    reset(FORM_DEFAULT_VALUES);
    hideConfirmDispenseModal();
  };

  const handleConfirmDispense = async () => {
    await confirmDispense();
    handleDialogClose();
  };

  return (
    <Dialog open={confirmDispenseModal.visible} onClose={handleDialogClose} maxWidth="md">
      <DialogTitle>Confirming dispense</DialogTitle>
      <DialogContent>
        <Stack direction={'column'} gap={theme.spacing(4)}>
          <Alert severity="warning">
            <AlertTitle>
              <u>Final step</u>: Once you confirm, this action cannot be undone.
            </AlertTitle>
            Confirming dispense will trigger invoicing, regulatory updates, and deduct inventory.
          </Alert>
          <Typography>
            Before confirming dispense please check to ensure you have completed the following steps:
          </Typography>
          <Box>
            <FormControlLabel
              control={<Checkbox {...register('stockOnHand', { required: 'Required' })} />}
              label={
                <Typography>
                  Checked that you have accurate physical <b>Stock On Hand (SOH)</b>
                </Typography>
              }
            />
            <FormControlLabel
              control={<Checkbox {...register('RTPMReviewed', { required: 'Required' })} />}
              label={
                <Typography>
                  Completed an <b>RTPM review</b>, confirming the <b>order is ready to dispense</b>
                </Typography>
              }
            />
            <FormControlLabel
              control={<Checkbox {...register('dispensedTokenAndPrintedPrescription', { required: 'Required' })} />}
              label={
                <Typography>
                  Successfully <b>dispensed the token and printed the prescription</b>
                </Typography>
              }
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="primary" onClick={handleDialogClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleConfirmDispense)}
          disabled={!formState.isValid}
          loading={isSubmitting}
        >
          Confirm dispense
        </Button>
      </DialogActions>
    </Dialog>
  );
}
